/* ============================================================
   sa-quotation.js — 御見積書  PDF download + misc helpers
   ============================================================ */
   (function () {
    'use strict';

    var LOG = '[sa-quotation]'; /* used by console.error only */

    var HONORIFIC_SUFFIXES = ['御中', '様', '殿', '先生'];

    function splitTitleSuffix(title) {
      var t = String(title || '').trim();
      if (!t) return { name: '', suffix: '' };
      for (var i = 0; i < HONORIFIC_SUFFIXES.length; i++) {
        var suf = HONORIFIC_SUFFIXES[i];
        if (t.slice(-suf.length) === suf && t.length > suf.length) {
          return { name: t.slice(0, -suf.length).trim(), suffix: suf };
        }
      }
      return { name: t, suffix: '' };
    }

    function renderCustomerFromParams(urlParams, root) {
      var scope    = root || document;
      var nameEl   = scope.querySelector('[data-sa-quotation-customer-name]');
      var suffixEl = scope.querySelector('[data-sa-quotation-customer-suffix]');
      var name     = urlParams.get('name');
      var suffix   = urlParams.get('suffix');
      if (nameEl && name) nameEl.textContent = name;
      if (suffixEl && suffix) suffixEl.textContent = suffix;
    }

    function normalizeCurrencyCode(value) {
      var code = String(value || '').trim().toUpperCase();
      return /^[A-Z]{3}$/.test(code) ? code : '';
    }

    function getStoredQuotationCurrency(token) {
      if (!token) return '';
      try {
        return normalizeCurrencyCode(localStorage.getItem('saQuotationCurrency:' + token));
      } catch (_) {
        return '';
      }
    }

    var urlParams = new URLSearchParams(window.location.search);

    /* ── 1. Quotation API data ────────────────────────────────
       Flow:
         a) Read ?token= from URL (or fallback token from data attr)
         b) GET /apps/plaud-quotation/api/quotations/{token}
         c) status="creating" → poll every 2 s (max 10 times)
         d) status="needs_reconcile" → POST .../draft-order then re-fetch
         e) status="expired" / 410 → show expired placeholder
         f) success → render quotation + draftOrder data into DOM
    ─────────────────────────────────────────────────────────── */

    (function fetchQuotationData() {
      var docs = document.querySelectorAll('[data-sa-quotation-doc]');
      if (!docs.length) return;
      docs.forEach(initQuotationDoc);

      function initQuotationDoc(doc) {
      renderCustomerFromParams(urlParams, doc);

      var token     = urlParams.get('token') || '';
      var apiBase   = (doc.dataset.apiBase || '/apps/plaud-quotation/api').replace(/\/$/, '');
      var fallback  = (doc.dataset.fallbackToken || '').trim();
      var taxRate   = normalizeTaxRate(doc.dataset.taxRate);

      if (!token) token = fallback;
      if (!token) {
        revealQuotationAmounts();
        return; /* no token available — keep server-rendered data */
      }

      var pollCount = 0;
      var MAX_POLLS = 10;

      function load() {
        fetch(apiBase + '/quotations/' + encodeURIComponent(token), {
          credentials: 'same-origin',
        })
          .then(function (res) {
            if (res.status === 410) { renderExpired(); return null; }
            return res.json().catch(function () {
              return {};
            }).then(function (payload) {
              if (!res.ok) {
                var code = payload && payload.code;
                if (code === 'QUOTATION_NOT_FOUND' || code === 'TOKEN_INVALID') renderExpired();
                else revealQuotationAmounts();
                return null;
              }
              return payload;
            });
          })
          .then(function (payload) {
            if (!payload) return;
            var quotation  = payload.quotation  || {};
            var draftOrder = payload.draftOrder || null;
            var meta       = payload.meta       || {};
            var status     = quotation.status   || 'ready';

            if (status === 'creating') {
              pollCount++;
              if (pollCount < MAX_POLLS) setTimeout(load, 2000);
              else revealQuotationAmounts();
              return;
            }

            if (status === 'needs_reconcile') {
              fetch(apiBase + '/quotations/' + encodeURIComponent(token) + '/draft-order', {
                method: 'POST', credentials: 'same-origin',
              })
                .then(function (r) { return r.json(); })
                .then(function (d) {
                  if (d && d.quotationToken) token = d.quotationToken;
                  setTimeout(load, 1000);
                })
                .catch(function () { revealQuotationAmounts(); });
              return;
            }

            if (status === 'expired') { renderExpired(); return; }

            renderQuotation(quotation, draftOrder, meta);
          })
          .catch(function () { revealQuotationAmounts(); });
      }

      load();

      /* ── Render helpers ───────────────────────────────── */

      function renderQuotation(quotation, draftOrder, meta) {
        /* Customer info: URL params take priority (form / dev mode), else API title */
        var nameEl   = doc.querySelector('[data-sa-quotation-customer-name]');
        var suffixEl = doc.querySelector('[data-sa-quotation-customer-suffix]');
        if (!urlParams.get('name') && nameEl && quotation.title) {
          var parsed = splitTitleSuffix(quotation.title);
          nameEl.textContent = parsed.name;
          if (suffixEl && parsed.suffix) suffixEl.textContent = parsed.suffix;
        }

        /* Price unavailable */
        if (meta.priceSource === 'unavailable') {
          renderPriceUnavailable(meta.message || '');
          return;
        }

        if (!draftOrder) {
          revealQuotationAmounts();
          return;
        }

        var currency = resolveDisplayCurrency(quotation, draftOrder);
        var lines = draftOrder.lines;

        /* Table rows */
        var tbody = doc.querySelector('[data-sa-quotation-tbody]');
        if (tbody && Array.isArray(lines)) {
          tbody.innerHTML = '';
          lines.forEach(function (line, index) {
            var computedLine = computeLineAmounts(line, draftOrder, index);
            var skuHtml = (line.sku && line.sku !== 'Default Title')
              ? '<div class="sa-quotation__item-variant">' + _esc(line.sku) + '</div>'
              : '';
            var tr = document.createElement('tr');
            tr.innerHTML =
              '<td class="sa-quotation__col-name">'
                + '<div class="sa-quotation__item-title">' + _esc(line.title || '') + '</div>'
                + skuHtml
              + '</td>'
              + '<td class="sa-quotation__col-price"><span class="sa-quotation__price-wrap">'
                + _fmtMoney(computedLine.unitPrice, currency)
              + '</span></td>'
              + '<td class="sa-quotation__col-qty">' + computedLine.quantity + '</td>'
              + '<td class="sa-quotation__col-discount"><span class="sa-quotation__price-wrap">'
                + _fmtDiscount(computedLine.discountTotal, currency)
              + '</span></td>'
              + '<td class="sa-quotation__col-amount"><span class="sa-quotation__price-wrap">'
                + _fmtMoney(computedLine.lineTotal, currency)
              + '</span></td>';
            tbody.appendChild(tr);
          });
        }

        /* Summary amounts */
        var totals = computeTotals(draftOrder);
        if (totals) {
          var subtotalEl   = doc.querySelector('[data-sa-quotation-subtotal]');
          var taxEl        = doc.querySelector('[data-sa-quotation-tax]');
          var taxRowEl     = doc.querySelector('[data-sa-quotation-tax-row]');
          var totalEl      = doc.querySelector('[data-sa-quotation-total]');
          var grandTotalEl = doc.querySelector('[data-sa-quotation-grand-total]');
          if (subtotalEl)   subtotalEl.textContent   = _fmtMoney(totals.subtotal,   currency);
          if (totalEl)      totalEl.textContent      = _fmtMoney(totals.grandTotal, currency);
          if (grandTotalEl) grandTotalEl.textContent = _fmtMoney(totals.grandTotal, currency);
          if (taxEl && hasNumericAmount(totals.taxTotal)) {
            taxEl.textContent = _fmtMoney(totals.taxTotal, currency);
            if (taxRowEl) taxRowEl.hidden = false;
          } else if (taxRowEl) {
            taxRowEl.hidden = true;
          }
          revealQuotationAmounts();
        } else {
          revealQuotationAmounts();
        }
      }

      function renderExpired() {
        var grandTotalEl = doc.querySelector('[data-sa-quotation-grand-total]');
        if (grandTotalEl) grandTotalEl.textContent = '—';
        clearSummaryAmounts('—');
        var taxRowEl = doc.querySelector('[data-sa-quotation-tax-row]');
        if (taxRowEl) taxRowEl.hidden = true;
        var tbody = doc.querySelector('[data-sa-quotation-tbody]');
        if (tbody) tbody.innerHTML = '<tr><td colspan="5" class="sa-quotation__table-empty"></td></tr>';
        revealQuotationAmounts();
      }

      function renderPriceUnavailable(message) {
        var grandTotalEl = doc.querySelector('[data-sa-quotation-grand-total]');
        if (grandTotalEl) grandTotalEl.textContent = message || '—';
        clearSummaryAmounts(message || '—');
        var taxRowEl = doc.querySelector('[data-sa-quotation-tax-row]');
        if (taxRowEl) taxRowEl.hidden = true;
        var tbody = doc.querySelector('[data-sa-quotation-tbody]');
        if (tbody) {
          tbody.innerHTML = '<tr><td colspan="5" class="sa-quotation__table-empty">'
            + _esc(message || '') + '</td></tr>';
        }
        revealQuotationAmounts();
      }

      function resolveDisplayCurrency(quotation, draftOrder) {
        return normalizeCurrencyCode(urlParams.get('currency') || urlParams.get('currencyCode'))
          || getStoredQuotationCurrency(token)
          || normalizeCurrencyCode(quotation && (quotation.currencyCode || quotation.currency))
          || normalizeCurrencyCode(draftOrder && (draftOrder.presentmentCurrencyCode || draftOrder.currencyCode))
          || 'USD';
      }

      function hasNumericAmount(amount) {
        return !isNaN(parseFloat(amount));
      }

      function normalizeTaxRate(value) {
        var n = parseFloat(value);
        return isNaN(n) || n < 0 ? 10 : n;
      }

      function revealQuotationAmounts() {
        doc.classList.remove('is-loading-quotation');
      }

      function clearSummaryAmounts(value) {
        var subtotalEl = doc.querySelector('[data-sa-quotation-subtotal]');
        var taxEl = doc.querySelector('[data-sa-quotation-tax]');
        var totalEl = doc.querySelector('[data-sa-quotation-total]');
        if (subtotalEl) subtotalEl.textContent = value;
        if (taxEl) taxEl.textContent = '';
        if (totalEl) totalEl.textContent = value;
      }

      function taxMultiplier() {
        return 1 + (taxRate / 100);
      }

      function roundMoney(amount) {
        var n = parseFloat(amount);
        return isNaN(n) ? null : Math.round(n * 100) / 100;
      }

      function taxExclusive(amount) {
        var n = parseFloat(amount);
        return isNaN(n) ? null : roundMoney(n / taxMultiplier());
      }

      function firstNumeric() {
        for (var i = 0; i < arguments.length; i++) {
          var n = numericAmount(arguments[i]);
          if (n != null) return n;
        }
        return null;
      }

      function numericAmount(value) {
        if (value == null || value === '') return null;
        if (typeof value === 'number') return isNaN(value) ? null : value;
        if (typeof value === 'string') {
          var cleaned = value.replace(/[^0-9.-]/g, '');
          if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
          var parsed = parseFloat(cleaned);
          return isNaN(parsed) ? null : parsed;
        }
        if (typeof value === 'object') {
          return firstNumeric(
            value.amount,
            value.value,
            value.total,
            value.totalAmount,
            value.shopMoney && value.shopMoney.amount,
            value.presentmentMoney && value.presentmentMoney.amount,
            value.shop_money && value.shop_money.amount,
            value.presentment_money && value.presentment_money.amount
          );
        }
        return null;
      }

      function apiDiscountAmount(line) {
        var direct = firstNumeric(
          line && line.discountTotal,
          line && line.discounttotal,
          line && line.discount,
          line && line.discountAmount,
          line && line.totalDiscount
        );
        if (direct != null) return Math.abs(direct);

        var allocations = line && (line.discountAllocations || line.discount_allocations);
        if (!Array.isArray(allocations)) return 0;

        return allocations.reduce(function (sum, allocation) {
          var amount = firstNumeric(
            allocation && allocation.amount,
            allocation && allocation.discountedAmount,
            allocation && allocation.allocatedAmount
          );
          return sum + Math.abs(amount || 0);
        }, 0);
      }

      function draftOrderDiscountAmount(draftOrder) {
        var apiTotals = draftOrder && draftOrder.totals || {};
        var direct = firstNumeric(
          apiTotals.discountTotal,
          apiTotals.discounttotal,
          apiTotals.discount,
          apiTotals.discountAmount,
          apiTotals.totalDiscount,
          draftOrder && draftOrder.discountTotal,
          draftOrder && draftOrder.discounttotal,
          draftOrder && draftOrder.discount,
          draftOrder && draftOrder.discountAmount,
          draftOrder && draftOrder.totalDiscount
        );
        return direct == null ? null : Math.abs(direct);
      }

      function computeLineAmounts(line, draftOrder, index) {
        var quantity = Number(line && line.quantity) || 0;
        var orderDiscount = draftOrderDiscountAmount(draftOrder);
        var apiDiscount = orderDiscount != null
          ? (index === 0 ? orderDiscount : 0)
          : apiDiscountAmount(line);
        var grossLineTotal = firstNumeric(
          line && line.lineTotal,
          line && line.total,
          line && line.totalPrice,
          line && line.amount
        );
        var grossUnitPrice = firstNumeric(
          line && line.originalUnitPrice,
          line && line.originalPrice,
          line && line.unitPrice,
          line && line.price,
          quantity > 0 && grossLineTotal != null ? (grossLineTotal + apiDiscount) / quantity : null
        );

        if (grossLineTotal == null && grossUnitPrice != null && quantity > 0) {
          grossLineTotal = Math.max(0, (grossUnitPrice * quantity) - apiDiscount);
        }

        return {
          quantity: quantity,
          unitPrice: taxExclusive(grossUnitPrice),
          discountTotal: apiDiscount || 0,
          lineTotal: taxExclusive(grossLineTotal),
        };
      }

      function computeTotals(draftOrder) {
        var apiTotals = draftOrder && draftOrder.totals || {};
        var grossTotal = firstNumeric(
          apiTotals.grandTotal,
          apiTotals.totalPrice,
          apiTotals.total,
          draftOrder && draftOrder.grandTotal,
          draftOrder && draftOrder.totalPrice
        );

        if (grossTotal == null && Array.isArray(draftOrder && draftOrder.lines)) {
          grossTotal = draftOrder.lines.reduce(function (sum, line) {
            var lineTotal = firstNumeric(line && line.lineTotal, line && line.total, line && line.totalPrice, 0);
            return sum + lineTotal;
          }, 0);
        }

        if (grossTotal == null) return null;

        var subtotal = taxExclusive(grossTotal);
        if (subtotal == null) return null;

        return {
          subtotal: subtotal,
          taxTotal: roundMoney(grossTotal - subtotal),
          grandTotal: roundMoney(grossTotal),
        };
      }

      function _fmtDiscount(amount, currencyCode) {
        var n = parseFloat(amount);
        if (isNaN(n) || n <= 0) return '-';
        return _fmtMoney(n, currencyCode);
      }

      function _fmtMoney(amount, currencyCode) {
        var n = parseFloat(amount);
        if (isNaN(n)) return String(amount != null ? amount : '—');
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: currencyCode || 'USD',
            minimumFractionDigits: 2,
          }).format(n);
        } catch (_) {
          return (currencyCode ? currencyCode + '\u00A0' : '') + n.toFixed(2);
        }
      }
      }
    })();

    /* Helper: escape HTML special chars for innerHTML insertion */
    function _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    /* ── 4. Print isolation ───────────────────────────────────── */
    if (!window.saQuotationPrintHandlersBound) {
      window.saQuotationPrintHandlersBound = true;
      window.addEventListener('beforeprint', function () {
        document.documentElement.classList.add('printing-quotation');
      });
      window.addEventListener('afterprint', function () {
        document.documentElement.classList.remove('printing-quotation');
      });
    }

    /* ── 5. PDF download button ───────────────────────────────── */
    var btn = document.querySelector('[data-sa-quotation-print-btn]');
    if (!btn) return;
    btn.addEventListener('click', handleDownload);

    /* ================================================================
       Utility helpers
       ================================================================ */

    function loadScript(src) {
      return new Promise(function (resolve, reject) {
        if (document.querySelector('script[src="' + src + '"]')) {
          var t = 0;
          var iv = setInterval(function () {
            t += 50;
            var ready = src.indexOf('html2canvas') !== -1
              ? typeof window.html2canvas === 'function'
              : !!(window.jspdf && window.jspdf.jsPDF);
            if (ready || t > 8000) { clearInterval(iv); resolve(); }
          }, 50);
          return;
        }
        var s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = function () { reject(new Error('Script load failed: ' + src)); };
        document.head.appendChild(s);
      });
    }

    function fetchCssText(cssUrl) {
      if (!cssUrl) return Promise.resolve('');
      return fetch(cssUrl, { mode: 'cors', cache: 'force-cache' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    }

    function getSaCssUrl() {
      var links = document.querySelectorAll('link[rel="stylesheet"]');
      for (var i = 0; i < links.length; i++) {
        if (links[i].href && links[i].href.indexOf('sa-quotation') !== -1) {
          /* 把 sa-quotation.css 换成 sa-quotation-pdf.css，加载 PDF 专用样式 */
          return links[i].href.replace('sa-quotation.css', 'sa-quotation-pdf.css');
        }
      }
      return null;
    }

    function getAnnouncementBg() {
      var sels = ['.w__AnnouncementBar', '.bls__announcement-bar',
        '#shopify-section-announcement-bar', '[id*="announcement-bar"]',
        '[data-section-type="announcement-bar"]', '.announcement-bar'];
      for (var i = 0; i < sels.length; i++) {
        var el = document.querySelector(sels[i]);
        if (!el) continue;
        var nodes = [el, el.firstElementChild];
        for (var j = 0; j < nodes.length; j++) {
          var n = nodes[j];
          if (!n) continue;
          var cs = window.getComputedStyle(n);
          var bg = cs.backgroundImage;
          if (bg && bg !== 'none') return bg;
          bg = cs.backgroundColor;
          if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') return bg;
        }
      }
      return 'linear-gradient(90deg,#4CD964 0%,#00C4CC 50%,#007AFF 100%)';
    }

    /* height 参数来自 LAYOUT.logo.height，方便统一控制 logo 大小 */
    function getLogoHTML(height) {
      var h = (height || 32) + 'px';
      var imgSels = ['header .header__heading-logo img', 'header [class*="logo"] img',
        '#shopify-section-header img', 'header img'];
      for (var i = 0; i < imgSels.length; i++) {
        var img = document.querySelector(imgSels[i]);
        if (img && img.src) {
          return '<img src="' + img.src + '" alt="' + (img.alt || 'PLAUD') + '"'
            + ' style="height:' + h + ';display:block;object-fit:contain;">';
        }
      }
      var svgSels = ['header [class*="logo"] svg', 'header svg'];
      for (var k = 0; k < svgSels.length; k++) {
        var svg = document.querySelector(svgSels[k]);
        if (svg) {
          var cl = svg.cloneNode(true);
          cl.setAttribute('style', 'height:' + h + ';width:auto;display:block;');
          return cl.outerHTML;
        }
      }
      return '<span style="font-size:22px;font-weight:700;letter-spacing:.05em">PLAUD</span>';
    }

    function genQNo() {
      var d = new Date(), p = function (n) { return String(n).padStart(2, '0'); };
      return 'P' + d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate())
        + p(d.getHours()) + p(d.getMinutes());
    }
    function fmtDateJa(d) {
      return d.getFullYear() + '年' + (d.getMonth() + 1) + '月' + d.getDate() + '日';
    }
    function fmtDateFile(d) {
      var p = function (n) { return String(n).padStart(2, '0'); };
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
    }

    /* ================================================================
       PDF 布局配置
       修改下面 LAYOUT 里的数值即可调整各元素的位置、大小、间距。
       PDF 画布 = 794 px 宽 × 1123 px 高（A4）。
       超出高度时内容会自动等比缩小塞进一页。
       ================================================================ */
    var LAYOUT = {

      /* ─── ① PLAUD logo ────────────────────────────────────────────
         top  = 距页面顶部的距离
         left = 距页面左边缘的距离
         height = logo 图片高度（宽度自动等比）                        */
      logo: { top: 24, left: 7, height: 26 },

      /* ─── ② 発行日 / 見積書番号（右上角）────────────────────────────
         top      = 距页面顶部（建议与 logo.top 保持一致）
         right    = 距页面右边缘
         fontSize = 字号
         lineHeight = 行高（控制发行日与见积书番号之间的行间距）         */
      headerDate: { top: 28, right: 7, fontSize: 10, lineHeight: 1.8 },

      /* ─── ③ logo行 → 彩色横条 的间距 ─────────────────────────────── */
      logoToBar: 10,              /* logo 行底部 到 彩色横条 的距离 px  */

      /* ─── ④ 彩色横条 ─────────────────────────────────────────────
         left    = 距页面左边缘
         width   = 横条长度
         height  = 横条粗细
         toTitle = 横条底部 到 御見積書标题 的距离                       */
      bar: { left: 7, width: 780, height: 2, toTitle:49 },

      /* ─── ⑤ 正文区整体左右边距 ──────────────────────────────────────
         left/right 控制：御見積書标题、客户信息、表格、备注 等
         所有正文内容与页面左/右边缘的距离                              */
      body: { left: 62, right: 62, paddingBottom: 41 },

      /* ─── ⑥ 御見積書 标题 ────────────────────────────────────────
         marginBottom = 标题下方 到 客户/公司信息行 的距离
         fontSize     = 字号                                           */
      title: { marginBottom: 34, fontSize: 26.6 },

      /* ─── ⑦ 客户名 + 様/御中 行（header 区块）───────────────────────
         marginBottom = header区（客户+公司）整体下方 到「下記の通り…」的距离
         nameSize     = 客户名字号
         suffixSize   = 様 / 御中 字号                                  */
      customer: { marginBottom: 40, nameSize: 17, suffixSize: 13 },

      /* ─── ⑧ 公司信息（右对齐）──────────────────────────────────────
         fontSize   = 字号
         lineHeight = 行高                                             */
      company: { fontSize: 10, lineHeight: 1.7 },

      /* ─── ⑨ 下記の通りお見積申し上げます ────────────────────────────
         marginBottom = 下方 到 合計金額 区块 的距离
         fontSize     = 字号                                           */
      intro: { marginBottom: 10, fontSize: 11 },

      /* ─── ⑩ 合計金額 行 ──────────────────────────────────────────
         marginBottom = 下方 到 商品表格 的距离
         labelSize    = 「合計金額」标签字号
         amountSize   = ¥金额数字字号
         noteSize     = 「（税込）」字号                                */
      total: { marginBottom: 60, labelSize: 13, amountSize: 14, noteSize: 10 },

      /* ─── ⑪ 商品表格 ─────────────────────────────────────────────
         marginBottom = 表格下方 到 小計/消費税/合計 区的距离
         fontSize     = 表格内字号                                     */
      table: { marginBottom: 28, fontSize: 10 },

      /* ─── ⑫ 小計 / 消費税 / 合計 区 ────────────────────────────────
         margin     = CSS margin（上 右 下 左）
         fontSize   = 字号
         lineHeight = 行高                                             */
      summary: { margin: '10px 0 20px', fontSize: 10, lineHeight: 1.4 },

      /* ─── ⑬ 備考（备注）────────────────────────────────────────────
         marginTop = 备注区 距上方内容（小計合計区块）的距离
         fontSize  = 字号                                              */
      notes: { marginTop: 400, fontSize: 9.5 },
    };

    /* ================================================================
       Build PDF HTML — 使用上方 LAYOUT 配置构建 HTML 字符串
       ================================================================ */
    function buildPdfHTML() {
      var today = new Date();
      var L = LAYOUT;
      var docEl = document.querySelector('.sa-quotation__doc');
      var docCloneHTML = '';

      if (docEl) {
        var clone = docEl.cloneNode(true);

        /* 移除下载按钮 */
        var dlBtn = clone.querySelector('.sa-quotation__download');
        if (dlBtn) dlBtn.parentNode.removeChild(dlBtn);

        /* ── 各模块间距（来自 LAYOUT）─────────────── */
        var titleEl = clone.querySelector('.sa-quotation__title');
        if (titleEl) titleEl.style.cssText += ';margin:0 0 ' + L.title.marginBottom + 'px;';

        var headerEl = clone.querySelector('.sa-quotation__header');
        if (headerEl) headerEl.style.cssText += ';margin-bottom:' + L.customer.marginBottom + 'px;';

        var introEl = clone.querySelector('.sa-quotation__intro');
        if (introEl) introEl.style.cssText += ';margin:0 0 ' + L.intro.marginBottom + 'px;';

        var totalHL = clone.querySelector('.sa-quotation__total-highlight');
        if (totalHL) totalHL.style.cssText += ';margin:0 0 ' + L.total.marginBottom + 'px;';

        var tableEl = clone.querySelector('.sa-quotation__table');
        if (tableEl) tableEl.style.cssText += ';margin:0 0 ' + L.table.marginBottom + 'px;';

        var summary = clone.querySelector('.sa-quotation__summary');
        if (summary) summary.style.cssText += ';margin:' + L.summary.margin + ';';

        var summaryRows = clone.querySelectorAll('.sa-quotation__summary-row');
        summaryRows.forEach(function (r, i) {
          /* 先把 span:last-child 的 min-width 撤掉，否则金额会被撑宽 */
          var lastSpan = r.querySelector('span:last-child');
          if (lastSpan) { lastSpan.style.minWidth = '0'; lastSpan.style.textAlign = 'left'; }

          r.style.display        = 'flex';
          r.style.justifyContent = 'flex-start';
          r.style.alignItems     = 'baseline';
          r.style.width          = 'max-content'; /* 行宽 = 内容宽，gap 才真正生效 */
          r.style.gap            = '8px';         /* ← 改这里调标签与金额的水平间距 */
          r.style.lineHeight     = String(L.summary.lineHeight);
          r.style.marginBottom   = i < summaryRows.length - 1 ? '10px' : '0';
          r.style.borderTop      = 'none';
          r.style.borderBottom   = 'none';
        });

        var notesEl = clone.querySelector('.sa-quotation__notes');
        if (notesEl) notesEl.style.cssText += ';margin-top:' + L.notes.marginTop + 'px;border-top:none;border:none;';

        /* 把 <ol><li> 转成 <table> 布局
           用 table 代替 flex：html2canvas 对 flex 多行文字换行位置计算有 bug，
           导致换行首字符与上一行末尾重叠；table 单元格位置计算是正确的。       */
        var notesListEl = clone.querySelector('.sa-quotation__notes-list');
        if (notesListEl) {
          var liItems = notesListEl.querySelectorAll('li');
          var noteFs = L.notes.fontSize + 'px';
          var tbl = document.createElement('table');
          tbl.style.cssText = 'border-collapse:collapse;width:100%;table-layout:fixed;';

          liItems.forEach(function (li, idx) {
            var tr = document.createElement('tr');

            /* 序号单元格 */
            var tdNum = document.createElement('td');
            tdNum.style.cssText = [
              'width:12px',
              'vertical-align:top',
              'white-space:nowrap',
              'padding:0 1px 4px 0',
              'font-size:' + noteFs,
              'line-height:1.6',
              'color:#333',
            ].join(';');
            tdNum.textContent = (idx + 1) + '.';

            /* 文字单元格：用 textContent 避免 innerHTML 可能带来的 HTML 实体问题 */
            var tdTxt = document.createElement('td');
            tdTxt.style.cssText = [
              'vertical-align:top',
              'padding:0 0 4px 0',
              'font-size:' + noteFs,
              'line-height:1.6',
              'color:#333',
              'word-break:break-all',
              'overflow-wrap:anywhere',
            ].join(';');
            /* textContent 设置纯文本；同时替换全角括弧为半角，防止字体未加载时仍错位 */
            tdTxt.textContent = li.textContent.trim()
              .replace(/\uFF08/g, '(')
              .replace(/\uFF09/g, ')');

            tr.appendChild(tdNum);
            tr.appendChild(tdTxt);
            tbl.appendChild(tr);
          });

          notesListEl.parentNode.replaceChild(tbl, notesListEl);
        }

        /* 全局字重；可选值：300（细）/ 400（常规）/ 500（中等）/ 700（粗）*/
        clone.style.fontWeight = '500';
        clone.querySelectorAll('*').forEach(function (el) {
          el.style.fontWeight = '500';
        });

        /* ── 字号 CSS 变量（来自 LAYOUT）──────────── */
        var vars = [
          '--sa-q-doc-max-width:100%',
          '--sa-q-title:'        + L.title.fontSize    + 'px',
          '--sa-q-name:'         + L.customer.nameSize + 'px',
          '--sa-q-suffix:'       + L.customer.suffixSize + 'px',
          '--sa-q-amount-label:' + L.total.labelSize   + 'px',
          '--sa-q-amount:'       + L.total.amountSize  + 'px',
          '--sa-q-body:'         + L.table.fontSize    + 'px',
          '--sa-q-summary:'      + L.summary.fontSize  + 'px',
          '--sa-q-notes:'        + L.notes.fontSize    + 'px',
          /* 颜色变量（必须在这里定义，否则 iframe 里 var() 无法解析）*/
          '--sa-q-ink:#000',
          '--sa-q-border:#000',
          '--sa-q-border-light:#e0e0e0',
          '--sa-q-muted:#555',
        ].join(';');
        var existing = clone.getAttribute('style') || '';
        clone.setAttribute('style', existing + ';max-width:100%;margin:0;' + vars);

        docCloneHTML = clone.outerHTML;
      } else {
        console.error(LOG, '.sa-quotation__doc not found');
      }

      return [
        '<div id="sa-pdf-root" style="width:794px;background:#fff;padding:0;box-sizing:border-box;font-family:\'Noto Sans JP\',\'Hiragino Sans\',Meiryo,sans-serif;font-weight:500;color:#000;">',

          /* ① logo + 発行日 + 見積書番号 */
          '<div style="display:flex;justify-content:space-between;align-items:flex-start;',
            'padding:' + L.logo.top + 'px ' + L.headerDate.right + 'px 0 ' + L.logo.left + 'px;',
            'margin-bottom:' + L.logoToBar + 'px;',
          '">',
            getLogoHTML(L.logo.height),
            '<div style="text-align:right;font-size:' + L.headerDate.fontSize + 'px;line-height:' + L.headerDate.lineHeight + ';">',
              '<div>発行日：　　' + fmtDateJa(today) + '</div>',
              '<div>見積書番号：' + genQNo() + '</div>',
            '</div>',
          '</div>',

          /* ② 彩色横条 */
          '<div style="',
            'height:' + L.bar.height + 'px;',
            'width:' + L.bar.width + 'px;',
            'margin-left:' + L.bar.left + 'px;',
            'margin-bottom:' + L.bar.toTitle + 'px;',
            'background:' + getAnnouncementBg() + ';',
          '"></div>',

          /* ③ 见积书正文 */
          '<div style="padding:0 ' + L.body.right + 'px ' + L.body.paddingBottom + 'px ' + L.body.left + 'px;">',
            docCloneHTML,
          '</div>',

        '</div>',
      ].join('');
    }

    /* ================================================================
       Render HTML inside a clean <iframe> with INLINED CSS.
       ================================================================ */
    function captureInIframe(html, cssText) {
      return new Promise(function (resolve, reject) {

        var iframe = document.createElement('iframe');
        /* No sandbox — we need unrestricted same-origin access for html2canvas */
        iframe.style.cssText = [
          'position:fixed', 'top:0', 'left:0',
          'width:794px', 'height:1200px',
          'border:none', 'visibility:hidden',
          'pointer-events:none', 'z-index:999999',
        ].join(';');

        document.body.appendChild(iframe);

        var idoc;
        try {
          idoc = iframe.contentDocument || iframe.contentWindow.document;
        } catch (e) {
          document.body.removeChild(iframe);
          reject(new Error('iframe contentDocument denied: ' + e.message));
          return;
        }
        if (!idoc) {
          document.body.removeChild(iframe);
          reject(new Error('iframe contentDocument is null'));
          return;
        }

        idoc.open();
        idoc.write([
          '<!DOCTYPE html><html><head>',
          '<meta charset="utf-8">',
          /* ← 真正下载 Noto Sans JP；没有这行 font-family 只是声明，字体不会被加载 */
          '<link rel="preconnect" href="https://fonts.googleapis.com">',
          '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>',
          '<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@100;200;300;400&display=swap" rel="stylesheet">',
          '<style>',
            'html,body{width:794px;max-width:794px;overflow:hidden;margin:0;padding:0;background:#fff;}',
            '*{box-sizing:border-box;}',
          '</style>',
          cssText ? ('<style>' + cssText + '</style>') : '',
          '</head><body>', html, '</body></html>',
        ].join(''));
        idoc.close();

        var A4_H = 1123; /* 297 mm at ~96 dpi */

        setTimeout(function () {
          var naturalH = idoc.documentElement.scrollHeight;

          var root = idoc.getElementById('sa-pdf-root') || idoc.body.firstElementChild;
          if (root && naturalH > A4_H) {
            var scl = A4_H / naturalH;
            root.style.transformOrigin = '50% 0';
            root.style.transform = 'scale(' + scl.toFixed(5) + ')';
          }

          /* Lock iframe to exactly A4 height */
          iframe.style.height   = A4_H + 'px';
          iframe.style.overflow = 'hidden';

          /* Phase 3: iframe 字体加载完后，把 FontFace 复制到父页面
             html2canvas 在父页面 Canvas 里调用 measureText()，必须父页面也有字体才能量准 */
          var iframeFonts = (idoc.fonts && idoc.fonts.ready) ? idoc.fonts.ready : Promise.resolve();
          Promise.all([iframeFonts, new Promise(function (r) { setTimeout(r, 500); })])
          .then(function () {
            /* 把 iframe 里所有已加载的 FontFace 复制到父页面 */
            if (idoc.fonts && idoc.fonts.forEach && document.fonts && document.fonts.add) {
              idoc.fonts.forEach(function (face) {
                if (face.status === 'loaded') {
                  try { document.fonts.add(face); } catch (e) { /* ignore duplicate */ }
                }
              });
            }
            /* 再等父页面把字体纳入后，调用 html2canvas */
            var parentReady = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
            return parentReady;
          })
          .then(function () {
            return html2canvas(idoc.body, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              x: 0,
              y: 0,
              scrollX: 0,
              scrollY: 0,
              width: 794,
              height: A4_H,
              windowWidth: 794,
              windowHeight: A4_H,
              logging: false,
            }).then(function (canvas) {
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
              resolve(canvas);
            }).catch(function (err) {
              console.error(LOG, 'html2canvas error:', err);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
              reject(err);
            });
          });
        }, 250);
      });
    }

    /* ================================================================
       Trigger file download.

       Theme Editor context: Shopify Admin embeds the storefront in an
       iframe and blocks ALL blob-URL navigations.  Detect this and show
       a clear message instead of crashing the editor preview.

       Live store: pdf.save() (jsPDF + FileSaver.js) is the most
       reliable cross-browser download trigger.
       ================================================================ */
    function triggerDownload(pdf, filename) {
      if (window.Shopify && window.Shopify.designMode) {
        alert(
          '【開発者向けメッセージ】\n' +
          'テーマエディター内ではPDFダウンロードは動作しません。\n' +
          '実際のストアページ（エディターを経由せず直接URL）でお試しください。\n\n' +
          '[Dev note] PDF download is blocked inside the Shopify Theme Editor iframe. ' +
          'Test on the live store URL directly.'
        );
        return;
      }

      /* Normal live-store download */
      try {
        pdf.save(filename);
      } catch (e) {
        console.error(LOG, 'pdf.save() failed:', e);
        alert('PDFの保存に失敗しました。\n' + e.message);
      }
    }

    /* ================================================================
       Main download handler
       ================================================================ */
    function handleDownload() {
      var originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = '生成中...';

      var cssUrl = getSaCssUrl();

      Promise.all([
        loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'),
        loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'),
        fetchCssText(cssUrl),
      ])
        .then(function (results) {
          if (typeof html2canvas !== 'function') throw new Error('html2canvas not available');
          var jsPDFCtor = (window.jspdf && window.jspdf.jsPDF) || window.jsPDF;
          if (!jsPDFCtor) throw new Error('jsPDF not available');

          var cssText = results[2] || '';
          var pdfHTML = buildPdfHTML();
          return captureInIframe(pdfHTML, cssText);
        })
        .then(function (canvas) {
          if (!canvas || canvas.width === 0 || canvas.height === 0) {
            throw new Error('html2canvas returned empty canvas ' + (canvas ? canvas.width + 'x' + canvas.height : 'null'));
          }

          var jsPDFCtor = (window.jspdf && window.jspdf.jsPDF) || window.jsPDF;
          var pdf = new jsPDFCtor({ orientation: 'portrait', unit: 'mm', format: 'a4' });
          var pdfW = pdf.internal.pageSize.getWidth();
          var pdfH = pdf.internal.pageSize.getHeight();

          var imgData = canvas.toDataURL('image/jpeg', 0.92);
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH);

          var filename = 'plaud_見積書_' + fmtDateFile(new Date()) + '.pdf';
          triggerDownload(pdf, filename);

          btn.disabled = false;
          btn.textContent = originalText;
        })
        .catch(function (err) {
          console.error(LOG, 'FAILED:', err);
          btn.disabled = false;
          btn.textContent = originalText;
          alert('PDFの生成に失敗しました。\nコンソールを確認してください。\n' + (err && err.message ? err.message : String(err)));
        });
    }
  })();
